import clonedeep from 'lodash.clonedeep';
import { calculateContent, scaleWidgets } from 'modules/Editor/store/infographSlice.helpers';
import { InfographState } from 'types/infographTypes';
import { WidgetsMap } from 'types/infographTypes';
import { Widget, WidgetType } from 'types/widget.types';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { getCombinedRect, getWidgetRect } from 'widgets/WidgetLayout.helpers';
import { WidgetRect } from 'widgets/WidgetLayout.types';

import { FULL_SIZE_OFFSET } from './ResponsiveLinkView.config';
import { DecorativeWidget, WidgetSegmentGroup } from './segmentAndGroupWidgets.types';

/**
 * Scales widget groups to fit the screen size.
 * (e.g. if the group is larger than the screen size, scale it to the screen size)
 *
 * @param windowSize - The size of the window.
 * @param segmentGroupArr - An array of widget segment groups.
 * @param infograph - The infograph state.
 * @param distanceToScreenLeft - The distance to the left of the screen.
 */
export const scaleGroups = (
  windowSize: { width: number; height: number },
  segmentGroupArr: WidgetSegmentGroup[],
  infograph: InfographState,
  distanceToScreenLeft: number,
) => {
  const widgets = infograph.widgets;
  const pageSize = infograph.size;

  const updateWidgets = [];

  // if its larger than the screen size, scale it to the screen size
  for (let i = 0; i < segmentGroupArr.length; i++) {
    const group = segmentGroupArr[i];

    // if the group is larger than the screen size, scale it to the screen size
    const isLargerThanScreenSize = group.widthPx > windowSize.width;
    if (isLargerThanScreenSize) {
      const previousSize = { widthPx: group.widthPx, heightPx: group.heightPx };
      const newSize = { widthPx: windowSize.width, heightPx: group.heightPx };

      // calculate the ratio for the size change
      const { xRatio, yRatio } = calculateContent(newSize, previousSize);

      // turn the group into a { widgetId: WidgetData } map
      const scaleWidgetsMap: WidgetsMap = {};
      group.members.forEach((m) => (scaleWidgetsMap[m.id] = clonedeep(widgets[m.id])));
      // add the container widget if the group has one
      if (group.id) scaleWidgetsMap[group.id] = clonedeep(widgets[group.id]);

      const scaledWidgetsArr = scaleWidgets(scaleWidgetsMap, [xRatio, yRatio], [0, 0]);
      if (!scaledWidgetsArr) continue;

      // Find the offset of the members to the original position
      // We're not changing the topPx of the group (but we're changing the topPx of the members)
      const scaledWidgetRect = getCombinedRect(scaledWidgetsArr.map((w) => getWidgetRect(w.widgetData as Widget)));
      const onlyMemberRects: WidgetRect[] = [];
      const onlyOriginalmemberCombinedRect: WidgetRect[] = [];
      scaledWidgetsArr.forEach((w) => {
        if (w.widgetId !== group.id) {
          onlyMemberRects.push(getWidgetRect(w.widgetData as Widget));
          onlyOriginalmemberCombinedRect.push(getWidgetRect(widgets[w.widgetId] as Widget));
        }
      });
      const scaledMemberRect = getCombinedRect(onlyMemberRects);
      const originalMemberRect = getCombinedRect(onlyOriginalmemberCombinedRect);
      const distanceToContainer = originalMemberRect.topPx - group.topPx;
      const diffDist = group.topPx - scaledMemberRect.topPx;
      const isFullWidth = group.widthPx + FULL_SIZE_OFFSET >= pageSize.widthPx;

      for (let j = 0; j < scaledWidgetsArr.length; j++) {
        const scaledWidget = scaledWidgetsArr[j];

        // if the widget is the group container
        // keep the original height and fit it to the screen width
        if (scaledWidget.widgetId === group.id) {
          const isImage = getWidgetTypeFromId(group.id) === WidgetType.Image;

          let newWidthPx = windowSize.width;

          if (isImage) {
            const imageWidgetData = scaleAndResetImageToNewSize(
              scaledWidget.widgetData as ImageWidgetData,
              windowSize.width,
              widgets[group.id].heightPx,
            );
            scaledWidget.widgetData = { ...imageWidgetData };
            newWidthPx = imageWidgetData.widthPx;
          }
          scaledWidget.widgetData.widthPx = newWidthPx;
          scaledWidget.widgetData.leftPx = 0;

          // keep the original position and height
          delete scaledWidget.widgetData.heightPx;
          delete scaledWidget.widgetData.topPx;
          continue;
        }

        // reset non-full width widgets to the left
        if (!isFullWidth) {
          scaledWidget.widgetData.leftPx = (scaledWidget.widgetData as Widget).leftPx - scaledWidgetRect.leftPx;
        }

        // reset the top position of the widgets
        scaledWidget.widgetData.topPx = (scaledWidget.widgetData as Widget).topPx + diffDist + distanceToContainer;
      }

      updateWidgets.push(...scaledWidgetsArr);
      // we don't need to scale the full width anymore since its smaller than the screen width
      continue;
    }

    // if the base widget is full width - update the width
    if (group.id) {
      const widget = widgets[group.id];
      const isFullWidth = widget.widthPx + FULL_SIZE_OFFSET >= pageSize.widthPx;
      if (!isFullWidth) continue;

      const type = getWidgetTypeFromId(group.id);
      const isShape = type === WidgetType.BasicShape;
      const isImage = type === WidgetType.Image;

      if (isShape && isFullWidth) {
        const newWidgetData = { widthPx: windowSize.width, leftPx: -distanceToScreenLeft };
        updateWidgets.push({ widgetId: group.id, widgetData: newWidgetData });
      }

      if (isImage && isFullWidth) {
        const newSize = scaleAndResetImageToNewSize(widget as ImageWidgetData, windowSize.width, widget.heightPx);
        const newWidgetData = { ...newSize, leftPx: -distanceToScreenLeft };
        updateWidgets.push({ widgetId: group.id, widgetData: newWidgetData });
      }
    }
  }

  return updateWidgets;
};

/**
 * Scales the decorative widgets to fit the screen size.
 *
 * @param windowSize - The size of the window containing the page.
 * @param decorativeWidgets - An array of decorative widgets.
 * @param widgets - A map of widgets.
 * @param distanceToScreenLeft - The distance from the left edge of the screen to the page.
 */
export const scaleDecorativeWidgets = (
  windowSize: { width: number; height: number },
  decorativeWidgets: DecorativeWidget[],
  widgets: WidgetsMap,
  distanceToScreenLeft: number,
) => {
  const windowWidth = windowSize.width;
  const windowHeight = windowSize.height;
  const updateWidgets = [];

  for (let i = 0; i < decorativeWidgets.length; i++) {
    const { id, type } = decorativeWidgets[i];
    const widgetType = getWidgetTypeFromId(id);

    if (widgetType === WidgetType.BasicShape) {
      // set the shape to full screen width
      if (type === 'full-width') {
        updateWidgets.push({ widgetId: id, widgetData: { widthPx: windowWidth, leftPx: -distanceToScreenLeft } });
      }

      // set the shape to full screen width
      if (type === 'full-height') {
        updateWidgets.push({
          widgetId: id,
          widgetData: { heightPx: windowHeight, topPx: 0 },
        });
      }

      // set the shape to full screen size
      if (type === 'full-size') {
        updateWidgets.push({
          widgetId: id,
          widgetData: { widthPx: windowWidth, heightPx: windowHeight, topPx: 0, leftPx: -distanceToScreenLeft },
        });
      }
    }

    if (widgetType === WidgetType.Image) {
      // set the image to full screen width
      if (type === 'full-width') {
        const newData = scaleAndResetImageToNewSize(widgets[id] as ImageWidgetData, windowWidth, widgets[id].heightPx);
        updateWidgets.push({
          widgetId: id,
          widgetData: { ...newData, leftPx: -distanceToScreenLeft },
        });
      }

      // set the image to full screen size
      if (type === 'full-size') {
        const newData = scaleAndResetImageToNewSize(widgets[id] as ImageWidgetData, windowWidth, windowHeight);
        updateWidgets.push({
          widgetId: id,
          widgetData: { ...newData, leftPx: -distanceToScreenLeft },
        });
      }
    }
  }

  return updateWidgets;
};

const scaleAndResetImageToNewSize = (image: ImageWidgetData, targetWidth: number, targetHeight: number) => {
  const currentWidth = image.widthPx;
  const currentHeight = image.heightPx;

  // Calculate the scaling factors
  const widthScale = targetWidth / currentWidth;
  const heightScale = targetHeight / currentHeight;

  // Use the larger scale factor to ensure the image is at least as large as the target dimensions
  const scaleFactor = Math.max(widthScale, heightScale);

  // Calculate the new dimensions
  const newWidth = currentWidth * scaleFactor;
  const newHeight = currentHeight * scaleFactor;

  return {
    widthPx: targetWidth,
    heightPx: targetHeight,
    imageRect: { widthPx: newWidth, heightPx: newHeight },
    // TODO: include crop in the new size
    crop: { top: 0, left: 0, right: 0, bottom: 0 },
  };
};

/**
 * Resets all widgets to their original state.
 *
 * @param originalWidgets - The original widgets map.
 */
export const resetAllWidgets = (originalWidgets: WidgetsMap) => {
  const updateWidgets = [];
  for (const widgetId in originalWidgets) {
    updateWidgets.push({
      widgetId,
      widgetData: { ...originalWidgets[widgetId], isHidden: !!originalWidgets[widgetId]?.isHidden },
    });
  }

  return updateWidgets;
};
